import axios, {AxiosResponse} from 'axios';
import {debug, frApiToken, frBaseUrl} from "../config";
import moment from 'moment';
import sha256 from 'crypto-js/sha256';
import hmacSHA512 from 'crypto-js/hmac-sha512';
import Base64 from 'crypto-js/enc-base64';
import fb from "../lib/firebase";
import {has} from "lodash";

const AuthenticationErrorClasses = ['AuthenticationError', 'SSOTokenExpiredError', 'TokenExpiredError', 'RequestTimeError']

export interface ApiResponseData {
  success: boolean;
  error: string;
  errorType?: string;
  backtrace?: string;
  data: any;
}

export interface ApiResponse extends AxiosResponse {
  data: ApiResponseData;
}

export interface AccessToken {
  authToken: string;
  user: {
    first_name: string;
    last_name: string;
    username: string;
  };
  date: string;
}

export class RequestError extends Error {
  response: object;

  constructor(msg: string, response: object) {
    super(msg);
    this.response = response;
  }
}

function checkStatus(response:any) {
  if (response.status >= 200 && response.status < 300)
    return response;

  console.error(`RequestError ${response.statusText}: ${response}`);
  throw new RequestError(response.statusText, response);
}

const getBaseUrl = (url: string|undefined) => {
  const re = new RegExp(frBaseUrl || '');
  const cleanup = (_url:string|undefined) => {
    if (!_url)
      return '';
    return _url.replace(re, '');
  }
  return cleanup(url);
}

function checkResponse(response: ApiResponse) {
  const {success, data} = response.data;
  if (success)
    debug(`[${response.status} OK] ${moment().format('HH:mm:ss')} ${getBaseUrl(response.config.url)}`, data);
  else
    debug(`[${response.status} FAILED] ${moment().format('HH:mm:ss')} ${getBaseUrl(response.config.url)}`, response.data);

  if (!success)
    throw new ApiError(response);

  return response.data;
}

interface RequestOptions {
  skipAuthToken: boolean;
}

export async function request(request:any, options?:RequestOptions): Promise<ApiResponseData> {
  /*
  Se temos um usuário autenticado, usar auth token no hash digest e enviar id do usuário
  no request
  */
  let skipAuthToken = false;
  if (options && has(options, 'skipAuthToken'))
    skipAuthToken = options.skipAuthToken;

  const currentUser = fb.auth.currentUser;
  let fbToken:string | undefined;
  if (currentUser) {
    fbToken = await currentUser.getIdToken();
    // console.log(`fbToken: ${fbToken}`);
  }

  if (!skipAuthToken && !fbToken)
    throw new Error('Invalid request, undefined fbToken');

  const time = moment.utc().format('YYYY-MM-DD HH:mm:ss');
  let hashDigest;
  let hmacDigest;

  let {params} = request;
  if (!params)
    params = {};

  // apeend auth data
  params.requestTime = time;

  if (skipAuthToken) {
    hashDigest = sha256(request.url + time);
    hmacDigest = Base64.stringify(hmacSHA512(hashDigest.toString(), frApiToken));
  } else {
    hmacDigest = fbToken;
  }

  const headers:any = {
    'Content-Type': 'application/json',
    Authorization: hmacDigest,
  };

  return axios({
    ...request,
    url: `${frBaseUrl}${request.url}`,
    params,
    headers
  })
    .then(checkStatus)
    .then(checkResponse)
    .catch(exception => {
      // TODO: tratar Network Error

      // check if exception is ApiError
      let errorMessage = 'Ocorreu um erro inesperado, impossível prosseguir.'
      if (exception instanceof ApiError)
        errorMessage = exception.message;
      else
        console.error(`REQUEST ERROR: ${exception}`);

      return {
        success: false,
        error: errorMessage,
        data: null,
      };
    });
}

export function post(url:string, data?:any, options?:RequestOptions): Promise<ApiResponseData> {
  return request({
    method: 'post',
    url,
    data,
  }, options);
}

export function get(url:string, params?:any): Promise<ApiResponseData> {
  return request({
    method: 'get',
    url,
    params,
  });
}

export class ApiError extends Error {
  response: ApiResponse;
  errorType: string;
  data: any;
  isAuthenticationError: boolean;

  constructor(response: ApiResponse) {
    const {error, errorType, data} = response.data;
    super(error);
    this.name = "ApiError";
    this.data = data;
    this.response = response;
    this.errorType = errorType || '';
    this.isAuthenticationError = false;

    if (errorType && AuthenticationErrorClasses.indexOf(errorType) !== -1)
      this.isAuthenticationError = true;
  }
}